import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
} from "recharts";
import {
  Calendar,
  TrendingUp,
  AlertTriangle,
  Package,
  Truck,
  Users,
  Clock,
  DollarSign,
  Activity,
  MapPin,
  Hourglass,
  Search,
  ArrowUpDown,
  Eye,
  ShoppingCart,
  History,
  Layers,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Check,
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { useProductionContext } from "../contexts/ProductionContext"; // <--- 1. USE CONTEXT
import { useCatalog } from "../hooks/useCatalog";
import { cleanSku, resolveCatalogSku } from "../utils/skuUtils";
import { checkIsSolicitacaoEstoque } from "../utils/orderUtils";

// Components
import AgeChart from "../components/dashboard/AgeChart";

// --- HELPERS ---
const getLocalDateKey = (dt) => {
  if (!dt) return "";
  const d = dt instanceof Date ? dt : new Date(dt);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatMoney = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const parseCurrency = (value) => {
  if (!value) return 0;
  try {
    if (typeof value === "number") return value;
    let str = String(value).trim().replace("R$", "").trim();
    if (str.includes(",")) str = str.replace(/\./g, "").replace(",", ".");
    const parsed = parseFloat(str);
    return isNaN(parsed) ? 0 : parsed;
  } catch (e) {
    return 0;
  }
};

const getOrderValue = (r) => {
  if (!r) return 0;
  // 1. Checa valores diretos
  if (r.total_value) return parseCurrency(r.total_value);

  const fd = r.full_data || {};

  // 2. Caminhos conhecidos no full_data
  let val = fd.order_metadata?.payment?.total ||
            fd.order?.payment?.total ||
            fd.total_value ||
            fd.total_amount ||
            fd.total_paid_amount ||
            fd.order?.total_amount ||
            fd.order?.total_paid_amount ||
            fd.payment?.total ||
            fd.payment?.total_paid;
  if (val) return parseCurrency(val);

  // 3. Checa array de payments no root
  if (Array.isArray(fd.payments) && fd.payments.length > 0) {
    let sum = 0;
    fd.payments.forEach(p => {
      sum += parseCurrency(p.transaction_amount || p.total_paid_amount || p.amount || 0);
    });
    if (sum > 0) return sum;
  }
  
  // 4. Checa array de payments dentro de order
  if (fd.order && Array.isArray(fd.order.payments) && fd.order.payments.length > 0) {
    let sum = 0;
    fd.order.payments.forEach(p => {
      sum += parseCurrency(p.transaction_amount || p.total_paid_amount || p.amount || 0);
    });
    if (sum > 0) return sum;
  }

  // 5. Checa itens do pedido (order_items) no root
  if (Array.isArray(fd.order_items) && fd.order_items.length > 0) {
    let sum = 0;
    fd.order_items.forEach(item => {
      const price = item.unit_price || item.full_unit_price || 0;
      const qty = item.quantity || 1;
      sum += parseCurrency(price) * qty;
    });
    if (sum > 0) return sum;
  }

  // 6. Checa itens do pedido (order_items) dentro de order
  if (fd.order && Array.isArray(fd.order.order_items) && fd.order.order_items.length > 0) {
    let sum = 0;
    fd.order.order_items.forEach(item => {
      const price = item.unit_price || item.full_unit_price || 0;
      const qty = item.quantity || 1;
      sum += parseCurrency(price) * qty;
    });
    if (sum > 0) return sum;
  }

  return 0;
};

const getShippingPrice = (r) => {
  if (!r) return 0;
  const fd = r.full_data || {};
  const val = fd.order?.shipping?.price ||
              fd.shipping?.price ||
              fd.shipping_price ||
              fd.order?.shipping_cost ||
              fd.shipping_cost ||
              0;
  return parseCurrency(val);
};

const checkIsMl = (orderNum, logisticsStatus, items = []) => {
  const num = String(orderNum || "").trim().toLowerCase();
  const isMlNum = /ML/i.test(num); // Se contém "ML" ou "ml" em qualquer lugar do número do pedido
  const isMlStatus = String(logisticsStatus || "").trim().toUpperCase() === "ML";
  const hasMlItem = items.some(i => String(i.logisticsStatus || "").trim().toUpperCase() === "ML");
  return isMlNum || isMlStatus || hasMlItem;
};

const checkIsRepostagem = (orderNum) => {
  const num = String(orderNum || "").trim().toLowerCase();
  return num.includes("repostagem") || num.includes("re-postagem");
};

const calculateOrderTotal = (info, findCatalogItem) => {
  if (info.paymentTotal > 0) return info.paymentTotal;
  
  // Se o valor total do pedido for 0, soma os preços dos itens no catálogo
  let sum = 0;
  info.items.forEach(item => {
    // Não soma itens cancelados ou golpes
    if (item.isCancelled || item.isGolpe) return;
    
    const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
    let itemPrice = 0;
    if (catalogItem && catalogItem.price) {
      itemPrice = parseFloat(catalogItem.price);
    } else if (item.price) {
      itemPrice = parseFloat(item.price);
    }
    sum += itemPrice;
  });
  return sum;
};

const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff8042",
  "#0088FE",
  "#00C49F",
  "#A020F0",
];

// --- MAPEAMENTO DE MODELOS E CURSOS (SKU PARSER) ---
const MODEL_MAP = {
  ATL: "Atlas",
  TRI: "Triunfo",
  AUR: "Aurora",
  NAV: "Navete",
  OCT: "Octogonal",
  OVA: "Oval",
  ORV: "Orvalia",
  VEG: "Vega",
  RED: "Redondo",
  MIR: "Mimir",
  PIN: "Pingente",
  BER: "Berloque",
  VIT: "Vitoria",
  LIA: "Lianora",
  GOT: "Gota",
  DON: "Donna",
  BEL: "Bella",
  MDL: "Medalha"
};

const COURSE_MAP = {
  ACU: "Acupuntura",
  ADM: "Administração",
  ADS: "Análise e Desenv. de Sistemas",
  AGG: "Agroecologia",
  AGN: "Agronegócio",
  AGR: "Agronomia",
  AQL: "Arquivologia",
  ARC: "Artes Cênicas",
  ARG: "Arqueologia",
  ARP: "Artes Plásticas",
  ARQ: "Arquitetura",
  ARV: "Artes Visuais",
  ASE: "Agente de Saúde e Endemias",
  AST: "Astrologia",
  ATP: "Antropologia",
  AVI: "Aviação",
  BAL: "Ballet",
  BAR: "Bartender",
  BIB: "Biblioteconomia",
  BIO: "Biologia",
  BME: "Biomedicina",
  BOM: "Bombeiro",
  BPC: "Bacteriologia Clínica",
  BRB: "Barbearia",
  BTG: "Biotecnologia",
  CAB: "Cabeleireira",
  CAC: "Comunicação das Artes do Corpo",
  CAG: "Ciências Agrárias",
  CAM: "Caminhoneiro",
  CBO: "Comissário de Bordo",
  CCP: "Ciência da Computação",
  CCT: "Ciências Contábeis",
  CEC: "Ciências Econômicas",
  CID: "Cuidador de Idosos",
  CIM: "Corretor de Imóveis",
  CIN: "Comunicação Social - Cinema",
  CMM: "Comunicação e Multimeios",
  COM: "Comércio Exterior",
  CPO: "Ciências Políticas",
  CRE: "Ciências da Religião",
  CSO: "Ciências Sociais",
  CSR: "Rádio - Comunicação Social",
  CTM: "Ciência e Tecnologia do Mar",
  DAN: "Design de Animação",
  DGR: "Design Gráfico",
  DIR: "Direito",
  DIT: "Design de Interiores",
  DMO: "Design de Moda",
  DPR: "Design de Produto",
  EAA: "Engenharia Agrícola e Ambiental",
  EAE: "Engenharia Aeroespacial",
  EAL: "Engenharia de Alimentos",
  EAQ: "Engenharia de Aquicultura",
  EAS: "Engenharia Ambiental e Sanitária",
  EBB: "Engenharia de Bioprocessos e Biotecnologia",
  EBM: "Engenharia Biomédica",
  EBQ: "Engenharia Bioquímica",
  ECA: "Engenharia Controle e Automação",
  ECI: "Engenharia Civil",
  ECP: "Engenharia Computação",
  EDC: "Edificação",
  EDF: "Educação Física",
  EEL: "Engenharia Elétrica",
  EER: "Engenharia de Energias Renováveis",
  EES: "Saúde e Estética",
  EFL: "Engenharia Florestal",
  EFU: "Ensino Fundamental",
  EJA: "Ensino Médio",
  EMA: "Engenharia de Materiais",
  EME: "Engenharia Mecânica",
  EMP: "Empreendedorismo",
  EMT: "Engenharia Metalúrgica",
  ENA: "Engenharia Aeronáutica",
  ENF: "Enfermagem",
  ENG: "Engenharia",
  ENO: "Enologia",
  ENQ: "Engenharia Química",
  EPR: "Engenharia de Produção",
  EPT: "Engenharia do Petróleo",
  ESP: "Ed. Especial",
  ESW: "Engenharia de Software",
  ETC: "Técnico em Eletrotécnica",
  ETL: "Engenharia de Telecomunicação",
  FAR: "Farmácia",
  FIL: "Filosofia",
  FIM: "Física Médica",
  FIN: "Gestão Financeira",
  FIS: "Física",
  FON: "Fonoaudiologia",
  FOT: "Fotografia",
  FTP: "Fisioterapia",
  GAL: "Gestão Comercial",
  GAM: "Gestão Ambiental",
  GAR: "Garçom/Maitre",
  GAS: "Gastronomia",
  GEA: "Geociência e Educação Ambiental",
  GEG: "Geografia",
  GEM: "Gestão Empresarial",
  GEO: "Geologia",
  GFT: "Gestão Fiscal e Tributária",
  GNI: "Gestão de Negócios Imobiliários",
  GPL: "Gestão Pública",
  GPO: "Gestão Portuária",
  GTL: "Gerontologia",
  HAR: "História da Arte",
  HID: "Engenharia Hídrica",
  HIS: "História",
  HOT: "Hotelaria",
  INC: "Instrumentação Cirúrgica",
  INF: "Educação Infantil",
  JOR: "Jornalismo",
  LBS: "Libras",
  LEC: "Licenciatura em Educação no Campo",
  LET: "Letras",
  LOG: "Logística",
  MAG: "Magistério",
  MAT: "Matemática",
  MCT: "Musicoterapia",
  MEC: "Mecatrônica",
  MED: "Medicina",
  MKT: "Marketing",
  MTE: "Massoterapia",
  MUS: "Música",
  NAT: "Naturologia",
  NCC: "Neurociência",
  NPP: "Neuropsicopedagogia",
  NUM: "Numerologia",
  NUT: "Nutrição",
  OCE: "Oceanografia",
  ODO: "Odontologia",
  OPT: "Óptica e Optometria",
  PAV: "Produção Audiovisual",
  PCR: "Perícia Criminal",
  PCT: "Produção Cultural",
  PDG: "Pedagogia",
  PGE: "Processos Gerenciais",
  PJG: "Programação de Jogos",
  POD: "Podologia",
  PPG: "Psicopedagogia",
  PSA: "Psicanálise",
  PSY: "Psicologia",
  PTD: "Prótese Dentária",
  PTX: "Produção Têxtil",
  PUB: "Publicidade",
  PVE: "Técnico em Produção de Vestuário",
  QAG: "Química Agroindustrial",
  QAM: "Química Ambiental",
  QPR: "Quiropraxia",
  QUI: "Química",
  RAD: "Radiologia",
  RCT: "Redes de Computadores",
  REH: "Recursos Humanos",
  RIN: "Relações Internacionais",
  RPU: "Relações Públicas",
  SCB: "Defesa Cibernética",
  SCL: "Sociologia",
  SCR: "Secretariado",
  SEC: "Educação Cristã",
  SET: "Segurança do Trabalho",
  SGH: "Gestão Hospitalar",
  SIN: "Sistemas de Informação",
  SJN: "Serviços Jurídicos e Notariais",
  SMV: "Sommelier de Vinho",
  SOC: "Serviço Social",
  SPR: "Segurança Privada",
  SPU: "Segurança Pública",
  TAE: "Tráfego Aéreo",
  TAG: "Técnico de Agropecuária",
  TAR: "Tarot",
  TAX: "Toxicologia Analítica",
  TEI: "TI",
  TEM: "Téc. em Mineração",
  TEN: "Técnico de Enfermagem",
  TEO: "Teologia",
  TET: "Técnico em Eletrônica",
  TIN: "Terapias Integrativas",
  TND: "Técnico em Nutrição e Dietética",
  TOC: "Terapia Ocupacional",
  TUR: "Turismo",
  UXD: "Design de Experiência de Usuário",
  VET: "Veterinária",
  VTT: "VetVet",
  ZOO: "Zootecnia"
};

const parseSku = (sku) => {
  if (!sku) return { course: "Estoque / Comum", model: "Outros" };
  
  const cleanSku = sku.trim().toUpperCase();
  const parts = cleanSku.split("-");
  
  // Curso: primeiro bloco antes do primeiro hífen
  const firstPart = parts[0] ? parts[0].trim() : "";
  let course = "Estoque / Comum";

  if (firstPart.length === 5) {
    course = "Religiosos";
  } else if (cleanSku.startsWith("SCR") || cleanSku.includes("MDL")) {
    course = "Religiosos";
  } else {
    if (firstPart.length === 3 && COURSE_MAP[firstPart]) {
      course = COURSE_MAP[firstPart];
    } else if (COURSE_MAP[firstPart]) {
      course = COURSE_MAP[firstPart];
    }
  }

  // Modelo: bloco após o primeiro hífen
  const secondPart = parts[1] ? parts[1].trim() : "";
  let model = "Outros";

  if (cleanSku.startsWith("SCR")) {
    model = "Outros"; // Ignorado para modelos
  } else if (cleanSku.startsWith("EXC")) {
    model = "Exclusivo";
  } else if (cleanSku.includes("ALI")) {
    model = "Aliança";
  } else if (cleanSku.includes("PUL")) {
    model = "Pulseira";
  } else if (cleanSku.startsWith("COR")) {
    model = "Corrente";
  } else if (cleanSku.startsWith("PERS") || cleanSku.includes("-ESC")) {
    model = "Escapulário";
  } else if (cleanSku.startsWith("ANL")) {
    model = "Anel";
  } else if (secondPart) {
    // Se o bloco após o hífen contiver número ou for Z/VTL, não é modelo (fica como Outros)
    const isExcluded = /\d/.test(secondPart) || ["Z", "VTL"].includes(secondPart);
    if (isExcluded) {
      model = "Outros";
    } else {
      model = MODEL_MAP[secondPart] || secondPart;
    }
  }

  return { course, model };
};

const getPeriodGoal = (start, end, goalsList) => {
  let totalGoal = 0;
  const current = new Date(start);
  const targetEnd = new Date(end);
  
  current.setHours(0, 0, 0, 0);
  targetEnd.setHours(0, 0, 0, 0);
  
  const seenMonths = new Set();
  const temp = new Date(current);
  while (temp <= targetEnd) {
    const mKey = `${temp.getFullYear()}-${temp.getMonth() + 1}`;
    if (!seenMonths.has(mKey)) {
      seenMonths.add(mKey);
      const mes = temp.getMonth() + 1;
      const ano = temp.getFullYear();
      const monthGoalRecord = goalsList.find(g => g.mes === mes && g.ano === ano);
      if (monthGoalRecord) {
        totalGoal += (monthGoalRecord.meta || 0);
      }
    }
    temp.setDate(temp.getDate() + 1);
  }
  return totalGoal;
};

export default function DashTab() {
  const { findItem: findCatalogItem } = useCatalog();
  // 2. GET DATA FROM CONTEXT (Shared Memory)
  const { orders: rawData, loading: loadingOrders } = useProductionContext();

  const [selectedRangeTab, setSelectedRangeTab] = useState("current_month");
  const [dateRange, setDateRange] = useState("current_month");
  
  // Custom date range states (Applied filter)
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  }, []);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);

  // Draft inputs for custom date picker (apenas aplicados ao clicar em OK)
  const [draftStartDate, setDraftStartDate] = useState(defaultStart);
  const [draftEndDate, setDraftEndDate] = useState(new Date().toISOString().split("T")[0]);

  const handleApplyCustomDates = () => {
    if (!draftStartDate || !draftEndDate) {
      alert("Por favor, preencha as datas inicial e final.");
      return;
    }
    const start = new Date(draftStartDate + "T00:00:00");
    const end = new Date(draftEndDate + "T23:59:59.999");
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      alert("Por favor, insira datas válidas.");
      return;
    }
    if (draftStartDate > draftEndDate) {
      alert("A data inicial não pode ser maior que a data final.");
      return;
    }
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setDateRange("custom");
  };

  const hasPendingDateChanges =
    selectedRangeTab === "custom" &&
    (dateRange !== "custom" || draftStartDate !== startDate || draftEndDate !== endDate);

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // Estados de análise de SKUs/Vendas
  const [currentView, setCurrentView] = useState("financial");
  const [soldItems, setSoldItems] = useState([]);
  const [archivedItems, setArchivedItems] = useState([]);
  const [loadingEnriched, setLoadingEnriched] = useState(false);
  const [searchSku, setSearchSku] = useState("");
  const [sortBy, setSortBy] = useState("sold"); // "sold" | "production" | "archived" | "total" | "key"
  const [groupBy, setGroupBy] = useState("sku"); // "sku" | "course" | "model"
  const [selectedSkuDetail, setSelectedSkuDetail] = useState(null);

  // Estados de Faturamento e Metas
  const [goals, setGoals] = useState([]);
  const [faturamentoOrders, setFaturamentoOrders] = useState([]);
  const [chartMode, setChartMode] = useState("cumulative"); // "cumulative" | "daily"
  const [financialSearchQuery, setFinancialSearchQuery] = useState("");
  const [financialPage, setFinancialPage] = useState(1);
  const [syncingMl, setSyncingMl] = useState(false);
  const [syncMlProgress, setSyncMlProgress] = useState("");
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Edit Goal Modal states
  const [isEditGoalModalOpen, setIsEditGoalModalOpen] = useState(false);
  const [editGoalMonth, setEditGoalMonth] = useState(() => new Date().getMonth() + 1);
  const [editGoalYear, setEditGoalYear] = useState(() => new Date().getFullYear());
  const [editGoalValue, setEditGoalValue] = useState("");
  const [isSavingGoal, setIsSavingGoal] = useState(false);

  useEffect(() => {
    if (isEditGoalModalOpen) {
      const existing = goals.find(g => g.mes === Number(editGoalMonth) && g.ano === Number(editGoalYear));
      setEditGoalValue(existing ? String(existing.meta) : "");
    }
  }, [isEditGoalModalOpen, editGoalMonth, editGoalYear, goals]);

  const handleSaveGoal = async (e) => {
    if (e) e.preventDefault();
    const val = parseFloat(editGoalValue);
    if (isNaN(val) || val < 0) return alert("Por favor, digite um valor de meta válido.");
    setIsSavingGoal(true);
    try {
      const existing = goals.find(g => g.mes === Number(editGoalMonth) && g.ano === Number(editGoalYear));
      const payload = {
        mes: Number(editGoalMonth),
        ano: Number(editGoalYear),
        meta: Number(val),
        status: "aberta"
      };
      
      let updated;
      if (existing) {
        updated = await pb.collection("pontuacao_metas").update(existing.id, payload);
      } else {
        updated = await pb.collection("pontuacao_metas").create(payload);
      }
      
      setGoals(prev => {
        const idx = prev.findIndex(g => g.mes === updated.mes && g.ano === updated.ano);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updated;
          return next;
        } else {
          return [...prev, updated];
        }
      });
      
      alert("Meta salva com sucesso!");
      setIsEditGoalModalOpen(false);
    } catch (err) {
      alert("Erro ao salvar meta: " + err.message);
    } finally {
      setIsSavingGoal(false);
    }
  };

  const handleSyncMlOrders = async () => {
    if (syncingMl) return;
    const confirmSync = window.confirm("Deseja sincronizar e preencher os preços dos pedidos do Mercado Livre de Agosto/2026 que estão com valor zerado no Droplet?");
    if (!confirmSync) return;

    setSyncingMl(true);
    setSyncMlProgress("Carregando catálogo...");

    try {
      // 1. Carrega Catálogo completo via PocketBase
      const catalogItems = await pb.collection("catalog").getFullList({
        fields: "sku,price,full_data"
      });
      
      const catalogMap = {};
      catalogItems.forEach(item => {
        if (!item.sku) return;
        const price = parseFloat(item.price || item.full_data?.price || 0);
        if (price > 0) {
          catalogMap[cleanSku(item.sku)] = item;
        }
      });

      setSyncMlProgress("Buscando pedidos de Agosto...");
      
      // 2. Busca pedidos de Agosto
      const filter = `(order_number ~ "ML" || order_number ~ "ml") && ((created >= "2026-08-01 00:00:00" && created <= "2026-08-31 23:59:59") || (full_data.convertedAt >= "2026-08-01" && full_data.convertedAt <= "2026-08-31"))`;
      const mlOrders = await pb.collection("production_orders").getFullList({
        filter,
        fields: "id,order_number,sku,total_value,created,full_data"
      });

      // 3. Filtra os que precisam de atualização
      const toUpdate = [];
      mlOrders.forEach(item => {
        const orderNum = item.order_number;
        const sku = item.sku;
        const fd = item.full_data || {};

        const dbVal = parseFloat(item.total_value || 0);
        const fdVal = parseFloat(fd.order?.payment?.total || 0);
        const currentVal = Math.max(dbVal, fdVal);

        if (currentVal === 0) {
          const resolved = resolveCatalogSku(sku, catalogMap);
          const catPrice = resolved ? parseFloat(resolved.price || resolved.full_data?.price || 0) : 0;
          if (catPrice && catPrice > 0) {
            toUpdate.push({
              id: item.id,
              order_number: orderNum,
              sku: sku,
              created: item.created,
              catalog_price: catPrice,
              full_data: fd
            });
          }
        }
      });

      if (toUpdate.length === 0) {
        alert("Todos os pedidos de Mercado Livre de Agosto/2026 já possuem valores válidos.");
        setSyncingMl(false);
        setSyncMlProgress("");
        return;
      }

      setSyncMlProgress(`Atualizando 0/${toUpdate.length}...`);
      
      // 4. Executa atualizações sequencialmente no PocketBase
      let count = 0;
      for (let i = 0; i < toUpdate.length; i++) {
        const o = toUpdate[i];
        const fd = { ...o.full_data };
        if (!fd.order) fd.order = {};
        if (!fd.order.payment) fd.order.payment = {};
        fd.order.payment.total = o.catalog_price;
        if (!fd.convertedAt) {
          fd.convertedAt = o.created;
        }

        try {
          await pb.collection("production_orders").update(o.id, {
            total_value: o.catalog_price,
            full_data: fd
          });
          count++;
          setSyncMlProgress(`Sincronizado ${count}/${toUpdate.length}...`);
        } catch (updateErr) {
          console.error(`Erro ao atualizar pedido ${o.order_number}:`, updateErr);
        }
      }

      alert(`Sincronização concluída! ${count} pedidos atualizados com sucesso.`);
      setReloadTrigger(prev => prev + 1);
    } catch (err) {
      alert("Erro na sincronização: " + err.message);
    } finally {
      setSyncingMl(false);
      setSyncMlProgress("");
    }
  };

  // --- FETCH LOGS (PocketBase) ---
  useEffect(() => {
    const fetchLogs = async () => {
      setLoadingLogs(true);
      try {
        // Busca apenas os logs mais recentes para performance
        const records = await pb.collection("system_logs").getList(1, 500, {
          sort: "-created",
        });

        // Adapter do log
        const mappedLogs = records.items.map(r => ({
          id: r.id,
          ...r,
          // data do gráfico: created object Date
          date: new Date(r.created)
        }));

        setLogs(mappedLogs);
      } catch (e) {
        console.error("Erro fetching logs:", e);
      } finally {
        setLoadingLogs(false);
      }
    };

    fetchLogs();

    // Realtime Logs
    pb.collection("system_logs").subscribe("*", (e) => {
      if (e.action === "create") {
        const newLog = {
          id: e.record.id,
          ...e.record,
          date: new Date(e.record.created)
        };
        setLogs(prev => [newLog, ...prev]); // Adiciona no topo
      }
    });

    return () => {
      pb.collection("system_logs").unsubscribe("*").catch(() => {});
    };
  }, []);

  // --- BUSCA DE DADOS ENRIQUECIDOS (VENDAS, ARQUIVADOS, METAS E FATURAMENTO) ---
  useEffect(() => {
    const loadEnrichedData = async () => {
      if (dateRange === "custom" && (!startDate || !endDate)) return;
      setLoadingEnriched(true);
      try {
        const now = new Date();
        let soldFilter = 'status = "sold"';
        let archivedFilter = 'archived = true';

        if (dateRange === "custom") {
          const startIso = new Date(startDate + "T00:00:00").toISOString();
          const endIso = new Date(endDate + "T23:59:59.999").toISOString();
          soldFilter = `status = "sold" && dateOut >= "${startIso}" && dateOut <= "${endIso}"`;
          archivedFilter = `archived = true && created >= "${startIso}" && created <= "${endIso}"`;
        } else if (dateRange === "prev_month") {
          const pmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
          const pmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
          const sIso = pmStart.toISOString();
          const eIso = pmEnd.toISOString();
          soldFilter = `status = "sold" && dateOut >= "${sIso}" && dateOut <= "${eIso}"`;
          archivedFilter = `archived = true && created >= "${sIso}" && created <= "${eIso}"`;
        } else if (dateRange !== "all") {
          const cutoff = new Date();
          if (dateRange === "current_month") {
            cutoff.setDate(1);
          } else {
            const days = parseInt(dateRange, 10);
            cutoff.setDate(now.getDate() - days);
          }
          cutoff.setHours(0, 0, 0, 0);
          const filterString = cutoff.toISOString();
          soldFilter = `status = "sold" && dateOut >= "${filterString}"`;
          archivedFilter = `archived = true && created >= "${filterString}"`;
        }

        // 1. Busca itens vendidos (inventory_items com status 'sold')
        const soldRecords = await pb.collection("inventory_items").getFullList({
          filter: soldFilter,
          fields: 'id,sku,dateOut,soldBy'
        });

        // 2. Busca pedidos arquivados (production_orders com archived = true)
        const archivedRecords = await pb.collection("production_orders").getFullList({
          filter: archivedFilter,
          fields: 'sku,created,order_number,customer_name,status,updated'
        });

        // 3. Busca metas de faturamento
        let goalsRecords = [];
        try {
          goalsRecords = await pb.collection("pontuacao_metas").getFullList({
            sort: "ano,mes"
          });
        } catch (errGoals) {
          console.warn("Erro ao buscar metas do PocketBase:", errGoals);
        }

        // 4. Busca pedidos para faturamento real (ativos e arquivados) baseado em anos calendários inteiros
        const currentYear = now.getFullYear();
        let targetYears = [currentYear];
        
        if (dateRange === "custom") {
          const sYear = new Date(startDate).getFullYear();
          const eYear = new Date(endDate).getFullYear();
          targetYears = [];
          for (let y = sYear - 1; y <= eYear; y++) {
            targetYears.push(y);
          }
        } else if (dateRange === "prev_month") {
          const pmYear = new Date(now.getFullYear(), now.getMonth() - 1, 1).getFullYear();
          targetYears = [pmYear - 1, pmYear];
        } else if (dateRange !== "all") {
          targetYears = [currentYear - 1, currentYear];
        } else {
          // all time - vamos buscar os anos desde 2024 até hoje
          targetYears = [];
          for (let y = 2024; y <= currentYear; y++) {
            targetYears.push(y);
          }
        }
        
        const faturamentoPromises = targetYears.map(year => {
          const startIso = `${year}-01-01T00:00:00.000Z`;
          const endIso = `${year}-12-31T23:59:59.999Z`;
          return pb.collection("production_orders").getFullList({
            filter: `(created >= "${startIso}" && created <= "${endIso}") || (full_data.convertedAt >= "${startIso}" && full_data.convertedAt <= "${endIso}")`,
            fields: "id,total_value,full_data,created,order_number,status,archived,customer_name"
          });
        });
        
        const results = await Promise.all(faturamentoPromises);
        const rawFlat = results.flat();
        
        // Deduplica por ID do registro
        const uniqueFaturamentoMap = new Map();
        rawFlat.forEach(r => {
          if (r && r.id) {
            uniqueFaturamentoMap.set(r.id, r);
          }
        });
        const faturamentoRecords = Array.from(uniqueFaturamentoMap.values());

        setSoldItems(soldRecords);
        setArchivedItems(archivedRecords);
        setGoals(goalsRecords);
        setFaturamentoOrders(faturamentoRecords);
      } catch (error) {
        console.error("Erro ao carregar dados de vendas/arquivados no dashboard:", error);
      } finally {
        setLoadingEnriched(false);
      }
    };

    loadEnrichedData();
  }, [dateRange, startDate, endDate, reloadTrigger]);

  // Reset pagination on filter or date range change
  useEffect(() => {
    setFinancialPage(1);
  }, [financialSearchQuery, dateRange, startDate, endDate]);

  // --- HEAVY CALCULATIONS (MEMOIZED) ---
  const data = useMemo(() => {
    // If context is loading, return null to show spinner
    if (!rawData || loadingOrders) return null;

    // 1. Define Cutoff Date
    const now = new Date();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    let cutoffDate = null;

    if (dateRange === "0") cutoffDate = todayStart;
    else if (dateRange === "current_month") {
      cutoffDate = new Date();
      cutoffDate.setDate(1);
      cutoffDate.setHours(0, 0, 0, 0);
    } else if (dateRange !== "all" && dateRange !== "custom" && dateRange !== "prev_month") {
      cutoffDate = new Date();
      cutoffDate.setDate(now.getDate() - parseInt(dateRange, 10));
      cutoffDate.setHours(0, 0, 0, 0);
    }

    // 2. Filter Items (Production Orders)
    const filteredItems = rawData.filter((item) => {
      // Se item.createdAt for string PB ou objeto Date
      let itemDate;
      try {
        if (item.createdAt && typeof item.createdAt === "string") {
          itemDate = new Date(item.createdAt);
        } else if (item.createdAt && item.createdAt.toDate) {
          itemDate = item.createdAt.toDate(); // Legado se sobrar algo na mem
        } else {
          itemDate = new Date(item.created || item.createdAt); // PB usa 'created'
        }
      } catch (e) { return false; }

      if (dateRange === "all") return true;
      if (dateRange === "custom") {
        if (!startDate || !endDate) return true;
        const start = new Date(startDate + "T00:00:00");
        const end = new Date(endDate + "T23:59:59.999");
        return itemDate >= start && itemDate <= end;
      }
      if (dateRange === "prev_month") {
        const pmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
        const pmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return itemDate >= pmStart && itemDate <= pmEnd;
      }
      return itemDate >= cutoffDate;
    });

    // 3. Filter Logs
    const filteredLogs = logs.filter((log) => {
      if (!log.date) return false;
      if (dateRange === "all") return true;
      if (dateRange === "custom") {
        if (!startDate || !endDate) return true;
        const start = new Date(startDate + "T00:00:00");
        const end = new Date(endDate + "T23:59:59.999");
        return log.date >= start && log.date <= end;
      }
      if (dateRange === "prev_month") {
        const pmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
        const pmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return log.date >= pmStart && log.date <= pmEnd;
      }
      return log.date >= cutoffDate;
    });

    // --- KPI CALCULATIONS ---
    const totalItems = filteredItems.length;
    const totalExceptions = filteredItems.filter((i) => i.full_data?.exception || i.exception).length;

    // Revenue Calculation
    const processedOrders = new Set();
    let totalRevenue = 0;

    // Using simple for loop for better performance on large arrays
    for (const item of filteredItems) {
      // Tenta ler do flat ou do full_data
      const orderIdentifier = item.order_number || item.order?.number || item.orderNumber;

      if (orderIdentifier && !processedOrders.has(orderIdentifier)) {
        processedOrders.add(orderIdentifier);
        // Não contabiliza solicitações de estoque internas como faturamento de cliente
        if (checkIsSolicitacaoEstoque(item)) continue;
        // Tenta ler valor mapeado do PB ou deep
        const val = item.total_value || item.order?.payment?.total;
        totalRevenue += parseCurrency(val);
      }
    }

    // --- CHART DATA PREPARATION ---

    // A. Daily Trend
    const dailyMap = {};
    filteredItems.forEach((item) => {
      const d = item.customCreatedAt
        ? new Date(item.customCreatedAt)
        : new Date(item.created || item.createdAt);

      d.setHours(0, 0, 0, 0);
      const key = d.getTime();
      dailyMap[key] = (dailyMap[key] || 0) + 1;
    });

    const trendData = Object.keys(dailyMap)
      .map((ts) => Number(ts))
      .sort((a, b) => a - b)
      .map((ts) => ({
        date: new Date(ts).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
        }),
        Qtd: dailyMap[ts],
      }));

    // B. UF (States)
    const ufMap = {};
    filteredItems.forEach((item) => {
      let uf =
        item.shipping_state || // Flattened PB
        item.shipping?.address?.statecode ||
        item.shipping?.address?.state ||
        "ND";

      uf = String(uf).toUpperCase().substring(0, 2);
      if (!uf || uf === "UN") uf = "ND";
      ufMap[uf] = (ufMap[uf] || 0) + 1;
    });

    let ufData = Object.entries(ufMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Group small values into "Others"
    if (ufData.length > 5) {
      const top = ufData.slice(0, 5);
      const others = ufData.slice(5).reduce((acc, curr) => acc + curr.value, 0);
      ufData = [...top, { name: "OUTROS", value: others }];
    }

    // C. Logistics Status
    const logMap = {};
    filteredItems.forEach((item) => {
      const status = (item.logisticsStatus || "SEM ETIQUETA")
        .toUpperCase()
        .trim();
      logMap[status] = (logMap[status] || 0) + 1;
    });
    const logisticsData = Object.keys(logMap)
      .map((name) => ({ name, value: logMap[name] }))
      .sort((a, b) => b.value - a.value);

    // D. Production Bottlenecks
    const prodMap = {};
    filteredItems.forEach((item) => {
      const status = item.status || "ND";
      // Clean up label
      const label = status.replace("ESTOQUE_", "").replace("_", " ");
      prodMap[label] = (prodMap[label] || 0) + 1;
    });
    const productionData = Object.keys(prodMap)
      .map((name) => ({ name, value: prodMap[name] }))
      .sort((a, b) => b.value - a.value);

    // E. Top Users (From Logs)
    const userMap = {};
    filteredLogs.forEach((log) => {
      const userName = log.user_name || log.user?.name || log.userName || "Desconhecido"; // Tenta flat ou deep
      if (userName !== "Sistema" && userName !== "Desconhecido") {
        userMap[userName] = (userMap[userName] || 0) + 1;
      }
    });

    const topUsers = Object.entries(userMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    return {
      totalItems,
      totalRevenue,
      totalExceptions,
      trendData,
      ufData,
      logisticsData,
      productionData,
      topUsers,
      filteredItems,
      totalActions: filteredLogs.length,
    };
  }, [rawData, logs, dateRange, startDate, endDate, loadingOrders]); // Dependencies

  // --- CÁLCULOS AGREGADOS POR SKU/CURSO/MODELO (MEMOIZED) ---
  const skuAnalysis = useMemo(() => {
    if (!data || !data.filteredItems) return [];
    
    const map = {};

    const getGroupKey = (sku) => {
      const parsed = parseSku(sku);
      if (groupBy === "course") return parsed.course;
      if (groupBy === "model") return parsed.model;
      return (sku || "INDEFINIDO").trim().toUpperCase();
    };

    // 1. Processar itens em produção ativa
    data.filteredItems.forEach((item) => {
      const key = getGroupKey(item.sku);
      if (!map[key]) {
        map[key] = { key, sold: 0, production: 0, archived: 0, total: 0 };
      }
      map[key].production += 1;
      map[key].total += 1;
    });

    // 2. Processar itens vendidos
    soldItems.forEach((item) => {
      const key = getGroupKey(item.sku);
      if (!map[key]) {
        map[key] = { key, sold: 0, production: 0, archived: 0, total: 0 };
      }
      map[key].sold += 1;
      map[key].total += 1;
    });

    // 3. Processar itens arquivados (passaram pelo fluxo)
    archivedItems.forEach((item) => {
      // Ignora ordens internas de reposição de estoque
      if (checkIsSolicitacaoEstoque(item)) return;
      const key = getGroupKey(item.sku);
      if (!map[key]) {
        map[key] = { key, sold: 0, production: 0, archived: 0, total: 0 };
      }
      map[key].archived += 1;
      map[key].total += 1;
    });

    let list = Object.values(map);

    // Filtrar por busca
    if (searchSku.trim()) {
      const term = searchSku.trim().toUpperCase();
      list = list.filter((item) => item.key.toUpperCase().includes(term));
    }

    // Ordenar
    list.sort((a, b) => {
      if (sortBy === "sku" || sortBy === "key") {
        return a.key.localeCompare(b.key);
      }
      return b[sortBy] - a[sortBy];
    });

    return list;
  }, [data, soldItems, archivedItems, searchSku, sortBy, groupBy, startDate, endDate]);

  // --- CÁLCULO DOS KPIS FINANCEIROS ---
  const financialKpis = useMemo(() => {
    const monthlyOrdersMap = new Map(); // Chave: `${orderNumber}_${year}_${month}`
    
    // Define limites de datas para filtrar os itens convertidos
    let start = null;
    let end = null;
    const now = new Date();
    
    if (dateRange === "custom") {
      if (startDate && endDate) {
        start = new Date(startDate + "T00:00:00");
        end = new Date(endDate + "T23:59:59.999");
      }
    } else if (dateRange === "current_month") {
      start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === "prev_month") {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      end = new Date(now.getFullYear(), now.getMonth() - 1, lastDay, 23, 59, 59, 999);
    } else if (dateRange !== "all") {
      const days = parseInt(dateRange, 10);
      start = new Date();
      start.setDate(now.getDate() - days);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
    }
    
    const isDateInSelectedRange = (d) => {
      if (dateRange === "all") return true;
      if (!start || !end) return true;
      return d >= start && d <= end;
    };
    
    // Agrupa pedidos e seus itens por número de pedido (evita duplicidades entre meses)
    faturamentoOrders.forEach(r => {
      const orderNum = r.order_number || r.full_data?.order?.number || r.full_data?.orderNumber || "S/N";
      if (checkIsRepostagem(orderNum)) return;
      if (checkIsSolicitacaoEstoque(r)) return; // Blindagem: ignora pedidos de reposição de estoque
      
      const key = (orderNum === "S/N" || !orderNum) ? `SN_${r.id}` : orderNum;

      if (!monthlyOrdersMap.has(key)) {
        monthlyOrdersMap.set(key, {
          orderNumber: orderNum,
          paymentTotal: getOrderValue(r),
          shippingPrice: getShippingPrice(r),
          customerName: r.customer_name || r.full_data?.customer_name || r.full_data?.order?.customer_name || "Cliente ND",
          created: r.created,
          status: r.status,
          logisticsStatus: r.logistics_status || r.full_data?.logisticsStatus || "",
          archived: !!r.archived,
          items: []
        });
      } else {
        const existing = monthlyOrdersMap.get(key);
        // Atualiza campos se vierem populados nas próximas linhas do mesmo pedido
        if (existing.paymentTotal === 0) {
          const val = getOrderValue(r);
          if (val > 0) existing.paymentTotal = val;
        }
        if (existing.shippingPrice === 0) {
          const ship = getShippingPrice(r);
          if (ship > 0) existing.shippingPrice = ship;
        }
        if (existing.customerName === "Cliente ND" || !existing.customerName) {
          const name = r.customer_name || r.full_data?.customer_name || r.full_data?.order?.customer_name;
          if (name && name !== "Cliente ND") existing.customerName = name;
        }
        if (!existing.logisticsStatus) {
          const logStat = r.logistics_status || r.full_data?.logisticsStatus;
          if (logStat) existing.logisticsStatus = logStat;
        }
      }
      
      const isCancelled = r.status === "CANCELADO" || r.full_data?.logisticsStatus === "CANCELADO";
      const isGolpe = r.status === "GOLPE" || r.full_data?.logisticsStatus === "GOLPE";
      
      monthlyOrdersMap.get(key).items.push({
        sku: r.sku,
        price: r.price || r.full_data?.price || 0,
        logisticsStatus: r.logistics_status || r.full_data?.logisticsStatus || "",
        fromStock: !!r.full_data?.fromStock,
        isCancelled,
        isGolpe,
        convertedAtStr: r.full_data?.convertedAt || r.created
      });
    });
    
    let totalRevenue = 0; // Com frete (Bruto)
    let totalRevenueNet = 0; // Sem frete (Líquido)
    let totalOrdersCount = 0;
    const contributingOrdersList = [];
    const processedOrdersSet = new Set();
    
    let mlRevenue = 0; // Com frete
    let mlRevenueNet = 0; // Sem frete
    let mlOrdersCount = 0;
    const mlProcessedOrders = new Set();
    
    let siteRevenue = 0; // Com frete
    let siteRevenueNet = 0; // Sem frete
    let siteOrdersCount = 0;
    const siteProcessedOrders = new Set();
    
    monthlyOrdersMap.forEach((info) => {
      const orderTotal = calculateOrderTotal(info, findCatalogItem);
      const netOrderTotal = Math.max(0, orderTotal - info.shippingPrice);
      
      // Filtra itens de fábrica do período selecionado
      const targetPeriodFactoryItems = info.items.filter(item => {
        if (item.fromStock || item.isCancelled || item.isGolpe) return false;
        if (item.convertedAtStr) {
          const d = new Date(item.convertedAtStr);
          return isDateInSelectedRange(d);
        }
        return false;
      });
      
      if (targetPeriodFactoryItems.length > 0) {
        const totalItemsCount = info.items.length || 1; // Denominador correto (itens do mesmo mês)
        const proportionalValGross = (orderTotal / totalItemsCount) * targetPeriodFactoryItems.length;
        const proportionalValNet = (netOrderTotal / totalItemsCount) * targetPeriodFactoryItems.length;
        
        totalRevenue += proportionalValGross;
        totalRevenueNet += proportionalValNet;
        
        if (!processedOrdersSet.has(info.orderNumber)) {
          processedOrdersSet.add(info.orderNumber);
          totalOrdersCount++;
        }
        
        // Separação por canal (Mercado Livre vs Site)
        const isMl = checkIsMl(info.orderNumber, info.logisticsStatus, info.items);
        if (isMl) {
          mlRevenue += proportionalValGross;
          mlRevenueNet += proportionalValNet;
          if (!mlProcessedOrders.has(info.orderNumber)) {
            mlProcessedOrders.add(info.orderNumber);
            mlOrdersCount++;
          }
        } else {
          siteRevenue += proportionalValGross;
          siteRevenueNet += proportionalValNet;
          if (!siteProcessedOrders.has(info.orderNumber)) {
            siteProcessedOrders.add(info.orderNumber);
            siteOrdersCount++;
          }
        }
        
        contributingOrdersList.push({
          orderNumber: info.orderNumber,
          customerName: info.customerName,
          date: new Date(info.created).toLocaleDateString("pt-BR"),
          originalTotal: orderTotal,
          shipping: info.shippingPrice,
          factoryItems: targetPeriodFactoryItems.length,
          totalItems: totalItemsCount,
          faturamentoProporcional: proportionalValGross, // Mostra o proporcional bruto na auditoria
          faturamentoProporcionalNet: proportionalValNet,
          status: info.status,
          archived: info.archived
        });
      }
    });
    
    // Combina entradas repetidas do mesmo pedido na tabela de auditoria (para visualização limpa)
    const uniqueOrdersAuditMap = new Map();
    contributingOrdersList.forEach(o => {
      if (!uniqueOrdersAuditMap.has(o.orderNumber)) {
        uniqueOrdersAuditMap.set(o.orderNumber, { ...o });
      } else {
        const existing = uniqueOrdersAuditMap.get(o.orderNumber);
        existing.factoryItems += o.factoryItems;
        existing.faturamentoProporcional += o.faturamentoProporcional;
        existing.faturamentoProporcionalNet += o.faturamentoProporcionalNet;
      }
    });
    
    const finalAuditList = Array.from(uniqueOrdersAuditMap.values());
    finalAuditList.sort((a, b) => String(b.orderNumber).localeCompare(String(a.orderNumber)));
    
    // Calcula a meta acumulada/proporcional para o período
    let totalGoal = 0;
    if (dateRange === "all") {
      totalGoal = goals.reduce((acc, curr) => acc + (curr.meta || 0), 0);
    } else if (start && end) {
      totalGoal = getPeriodGoal(start, end, goals);
    }
    
    const progressPercent = totalGoal > 0 ? (totalRevenue / totalGoal) * 100 : 0;
    const averageOrderValue = totalOrdersCount > 0 ? totalRevenue / totalOrdersCount : 0;
    const mlAverageValue = mlOrdersCount > 0 ? mlRevenue / mlOrdersCount : 0;
    const siteAverageValue = siteOrdersCount > 0 ? siteRevenue / siteOrdersCount : 0;
    
    return {
      totalRevenue,
      totalRevenueNet,
      totalGoal,
      progressPercent,
      totalOrdersCount,
      averageOrderValue,
      mlRevenue,
      mlRevenueNet,
      mlOrdersCount,
      mlAverageValue,
      siteRevenue,
      siteRevenueNet,
      siteOrdersCount,
      siteAverageValue,
      contributingOrders: finalAuditList
    };
  }, [faturamentoOrders, goals, dateRange, startDate, endDate]);

  // --- PREPARAÇÃO DE DADOS DO GRÁFICO FINANCEIRO (EVOLUÇÃO) ---
  const financialChartData = useMemo(() => {
    if (dateRange === "custom" && (!startDate || !endDate)) return [];
    
    let start, end;
    let prevStart, prevEnd;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    if (dateRange === "0") {
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      
      prevStart = new Date(start);
      prevStart.setDate(prevStart.getDate() - 1);
      prevEnd = new Date(end);
      prevEnd.setDate(prevEnd.getDate() - 1);
    } else if (dateRange === "current_month") {
      start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      
      prevStart = new Date(start);
      prevStart.setMonth(prevStart.getMonth() - 1);
      prevEnd = new Date(end);
      prevEnd.setMonth(prevEnd.getMonth() - 1);
    } else if (dateRange === "prev_month") {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      end = new Date(now.getFullYear(), now.getMonth() - 1, lastDay, 23, 59, 59, 999);

      prevStart = new Date(start.getFullYear(), start.getMonth() - 1, 1, 0, 0, 0, 0);
      const lastDayPrev = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0).getDate();
      prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth(), lastDayPrev, 23, 59, 59, 999);
    } else if (dateRange === "custom") {
      start = new Date(startDate + "T00:00:00");
      end = new Date(endDate + "T23:59:59.999");
      
      const isStartFirstDay = start.getDate() === 1;
      const lastDayOfEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
      const isEndLastDay = end.getDate() === lastDayOfEndMonth;
      const isSameMonthAndYear = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

      if (isStartFirstDay && isEndLastDay && isSameMonthAndYear) {
        // Mês inteiro no passado ou presente -> compara com o mês anterior
        prevStart = new Date(start.getFullYear(), start.getMonth() - 1, 1, 0, 0, 0, 0);
        const lastDayPrevMonth = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0).getDate();
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth(), lastDayPrevMonth, 23, 59, 59, 999);
      } else {
        const diffMs = end.getTime() - start.getTime();
        prevStart = new Date(start.getTime() - diffMs - 1000);
        prevEnd = new Date(end.getTime() - diffMs - 1000);
      }
    } else if (dateRange === "all") {
      if (faturamentoOrders.length === 0) return [];
      let minDate = new Date();
      faturamentoOrders.forEach(o => {
        if (checkIsSolicitacaoEstoque(o)) return;
        const d = new Date(o.full_data?.convertedAt || o.created);
        if (d < minDate) minDate = d;
      });
      start = new Date(minDate);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      
      const diffMs = end.getTime() - start.getTime();
      prevStart = new Date(start.getTime() - diffMs - 1000);
      prevEnd = new Date(end.getTime() - diffMs - 1000);
    } else {
      const days = parseInt(dateRange, 10);
      start = new Date();
      start.setDate(now.getDate() - days);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      
      const diffMs = end.getTime() - start.getTime();
      prevStart = new Date(start.getTime() - diffMs - 1000);
      prevEnd = new Date(end.getTime() - diffMs - 1000);
    }
    
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const groupByMonth = dateRange === "all" || diffDays > 45;
    const monthlyOrdersMap = new Map();
    faturamentoOrders.forEach(r => {
      const orderNum = r.order_number || r.full_data?.order?.number || r.full_data?.orderNumber || "S/N";
      if (checkIsRepostagem(orderNum)) return;
      if (checkIsSolicitacaoEstoque(r)) return; // Blindagem: ignora pedidos de reposição de estoque
      
      const key = (orderNum === "S/N" || !orderNum) ? `SN_${r.id}` : orderNum;

      if (!monthlyOrdersMap.has(key)) {
        monthlyOrdersMap.set(key, {
          orderNumber: orderNum,
          paymentTotal: getOrderValue(r),
          shippingPrice: getShippingPrice(r),
          customerName: r.customer_name || r.full_data?.customer_name || r.full_data?.order?.customer_name || "Cliente ND",
          created: r.created,
          status: r.status,
          logisticsStatus: r.logistics_status || r.full_data?.logisticsStatus || "",
          archived: !!r.archived,
          items: []
        });
      } else {
        const existing = monthlyOrdersMap.get(key);
        // Atualiza campos se vierem populados nas próximas linhas do mesmo pedido
        if (existing.paymentTotal === 0) {
          const val = getOrderValue(r);
          if (val > 0) existing.paymentTotal = val;
        }
        if (existing.shippingPrice === 0) {
          const ship = getShippingPrice(r);
          if (ship > 0) existing.shippingPrice = ship;
        }
        if (existing.customerName === "Cliente ND" || !existing.customerName) {
          const name = r.customer_name || r.full_data?.customer_name || r.full_data?.order?.customer_name;
          if (name && name !== "Cliente ND") existing.customerName = name;
        }
        if (!existing.logisticsStatus) {
          const logStat = r.logistics_status || r.full_data?.logisticsStatus;
          if (logStat) existing.logisticsStatus = logStat;
        }
      }
      
      const isCancelled = r.status === "CANCELADO" || r.full_data?.logisticsStatus === "CANCELADO";
      const isGolpe = r.status === "GOLPE" || r.full_data?.logisticsStatus === "GOLPE";
      
      monthlyOrdersMap.get(key).items.push({
        sku: r.sku,
        price: r.price || r.full_data?.price || 0,
        logisticsStatus: r.logistics_status || r.full_data?.logisticsStatus || "",
        fromStock: !!r.full_data?.fromStock,
        isCancelled,
        isGolpe,
        convertedAtStr: r.full_data?.convertedAt || r.created
      });
    });
    
    if (groupByMonth) {
      // Agrupamento Mensal
      const monthsMap = {};
      const current = new Date(start);
      current.setDate(1);
      current.setHours(0, 0, 0, 0);
      
      const targetEnd = new Date(end);
      targetEnd.setHours(0, 0, 0, 0);
      
      while (current <= targetEnd || (current.getMonth() === targetEnd.getMonth() && current.getFullYear() === targetEnd.getFullYear())) {
        const mKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}`;
        monthsMap[mKey] = {
          label: `${String(current.getMonth() + 1).padStart(2, "0")}/${current.getFullYear()}`,
          faturamento: 0, // Com frete
          faturamentoNet: 0, // Sem frete
          faturamentoAnterior: 0,
          faturamentoAnteriorNet: 0,
          mlFaturamento: 0,
          mlFaturamentoNet: 0,
          mlFaturamentoAnterior: 0,
          siteFaturamento: 0,
          siteFaturamentoNet: 0,
          siteFaturamentoAnterior: 0,
          goal: 0,
          year: current.getFullYear(),
          month: current.getMonth() + 1,
          ordersSet: new Set(),
          mlOrdersSet: new Set(),
          siteOrdersSet: new Set()
        };
        current.setMonth(current.getMonth() + 1);
      }
      
      // Associa metas mensais
      Object.keys(monthsMap).forEach(key => {
        const mInfo = monthsMap[key];
        const gRecord = goals.find(g => g.mes === mInfo.month && g.ano === mInfo.year);
        mInfo.goal = gRecord ? gRecord.meta : 0;
      });
      
      monthlyOrdersMap.forEach((info) => {
        const orderTotal = calculateOrderTotal(info, findCatalogItem);
        const netOrderTotal = Math.max(0, orderTotal - info.shippingPrice);
        const totalItemsCount = info.items.length || 1;
        const isMl = checkIsMl(info.orderNumber, info.logisticsStatus, info.items);
        
        info.items.forEach(item => {
          if (item.fromStock || item.isCancelled || item.isGolpe) return;
          if (item.convertedAtStr) {
            const d = new Date(item.convertedAtStr);
            const proportionalValGross = orderTotal / totalItemsCount;
            const proportionalValNet = netOrderTotal / totalItemsCount;
            
            // Faturamento atual
            if (d >= start && d <= end) {
              const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
              if (monthsMap[mKey]) {
                monthsMap[mKey].faturamento += proportionalValGross;
                monthsMap[mKey].faturamentoNet += proportionalValNet;
                monthsMap[mKey].ordersSet.add(info.orderNumber);
                if (isMl) {
                  monthsMap[mKey].mlFaturamento += proportionalValGross;
                  monthsMap[mKey].mlFaturamentoNet += proportionalValNet;
                  monthsMap[mKey].mlOrdersSet.add(info.orderNumber);
                } else {
                  monthsMap[mKey].siteFaturamento += proportionalValGross;
                  monthsMap[mKey].siteFaturamentoNet += proportionalValNet;
                  monthsMap[mKey].siteOrdersSet.add(info.orderNumber);
                }
              }
            }
            
            // Faturamento anterior (mês comparativo)
            if (d >= prevStart && d <= prevEnd) {
              const targetD = new Date(d);
              const startMonthCount = start.getFullYear() * 12 + start.getMonth();
              const prevStartMonthCount = prevStart.getFullYear() * 12 + prevStart.getMonth();
              const diffMonths = startMonthCount - prevStartMonthCount;
              targetD.setMonth(targetD.getMonth() + diffMonths);
              
              const mKey = `${targetD.getFullYear()}-${String(targetD.getMonth() + 1).padStart(2, "0")}`;
              if (monthsMap[mKey]) {
                monthsMap[mKey].faturamentoAnterior += proportionalValGross;
                monthsMap[mKey].faturamentoAnteriorNet += proportionalValNet;
                if (isMl) {
                  monthsMap[mKey].mlFaturamentoAnterior += proportionalValGross;
                } else {
                  monthsMap[mKey].siteFaturamentoAnterior += proportionalValGross;
                }
              }
            }
          }
        });
      });
      
      const list = Object.keys(monthsMap)
        .sort()
        .map(key => ({
          name: monthsMap[key].label,
          "Faturamento": Math.round(monthsMap[key].faturamento),
          "Faturamento Sem Frete": Math.round(monthsMap[key].faturamentoNet),
          "Faturamento Anterior": Math.round(monthsMap[key].faturamentoAnterior),
          "Faturamento Anterior Sem Frete": Math.round(monthsMap[key].faturamentoAnteriorNet),
          "ML Faturamento": Math.round(monthsMap[key].mlFaturamento),
          "ML Faturamento Sem Frete": Math.round(monthsMap[key].mlFaturamentoNet),
          "ML Faturamento Anterior": Math.round(monthsMap[key].mlFaturamentoAnterior),
          "Site Faturamento": Math.round(monthsMap[key].siteFaturamento),
          "Site Faturamento Sem Frete": Math.round(monthsMap[key].siteFaturamentoNet),
          "Site Faturamento Anterior": Math.round(monthsMap[key].siteFaturamentoAnterior),
          "Meta": Math.round(monthsMap[key].goal),
          ordersCount: monthsMap[key].ordersSet.size,
          mlOrdersCount: monthsMap[key].mlOrdersSet.size,
          siteOrdersCount: monthsMap[key].siteOrdersSet.size
        }));
        
      return list;
      
    } else {
      // Agrupamento Diário
      const isStartFirstDay = start.getDate() === 1;
      const lastDayOfEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
      const isEndLastDay = end.getDate() === lastDayOfEndMonth;
      const isSameMonthAndYear = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
      const isFullMonth = (isStartFirstDay && isEndLastDay && isSameMonthAndYear) || dateRange === "current_month" || dateRange === "prev_month";

      const daysMap = {};
      const current = new Date(start);
      const targetEnd = new Date(end);
      
      while (current <= targetEnd) {
        const dKey = getLocalDateKey(current);
        const formattedDate = isFullMonth 
          ? String(current.getDate()).padStart(2, "0") 
          : current.toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit"
            });
        
        const mes = current.getMonth() + 1;
        const ano = current.getFullYear();
        const gRecord = goals.find(g => g.mes === mes && g.ano === ano);
        const daysInMonth = new Date(ano, mes, 0).getDate();
        const dailyGoal = gRecord ? gRecord.meta / daysInMonth : 0;
        
        daysMap[dKey] = {
          label: formattedDate,
          dateObj: new Date(current),
          faturamento: 0, // Com frete
          faturamentoNet: 0, // Sem frete
          faturamentoAnterior: 0,
          faturamentoAnteriorNet: 0,
          mlFaturamento: 0,
          mlFaturamentoNet: 0,
          mlFaturamentoAnterior: 0,
          siteFaturamento: 0,
          siteFaturamentoNet: 0,
          siteFaturamentoAnterior: 0,
          goal: dailyGoal,
          ordersSet: new Set(),
          mlOrdersSet: new Set(),
          siteOrdersSet: new Set()
        };
        current.setDate(current.getDate() + 1);
      }
      
      monthlyOrdersMap.forEach((info) => {
        const orderTotal = calculateOrderTotal(info, findCatalogItem);
        const netOrderTotal = Math.max(0, orderTotal - info.shippingPrice);
        const totalItemsCount = info.items.length || 1;
        const isMl = checkIsMl(info.orderNumber, info.logisticsStatus, info.items);
        
        info.items.forEach(item => {
          if (item.fromStock || item.isCancelled || item.isGolpe) return;
          if (item.convertedAtStr) {
            const d = new Date(item.convertedAtStr);
            const proportionalValGross = orderTotal / totalItemsCount;
            const proportionalValNet = netOrderTotal / totalItemsCount;
            
            // Faturamento atual
            if (d >= start && d <= end) {
              const dKey = getLocalDateKey(d);
              if (daysMap[dKey]) {
                daysMap[dKey].faturamento += proportionalValGross;
                daysMap[dKey].faturamentoNet += proportionalValNet;
                daysMap[dKey].ordersSet.add(info.orderNumber);
                if (isMl) {
                  daysMap[dKey].mlFaturamento += proportionalValGross;
                  daysMap[dKey].mlFaturamentoNet += proportionalValNet;
                  daysMap[dKey].mlOrdersSet.add(info.orderNumber);
                } else {
                  daysMap[dKey].siteFaturamento += proportionalValGross;
                  daysMap[dKey].siteFaturamentoNet += proportionalValNet;
                  daysMap[dKey].siteOrdersSet.add(info.orderNumber);
                }
              }
            }
            
            // Faturamento anterior (mês comparativo)
            if (d >= prevStart && d <= prevEnd) {
              let targetDKey = "";
              if (isFullMonth) {
                const currentDay = d.getDate();
                const matchedDayEntry = Object.keys(daysMap).find(key => daysMap[key].dateObj.getDate() === currentDay);
                if (matchedDayEntry) targetDKey = matchedDayEntry;
              } else {
                const offsetMs = d.getTime() - prevStart.getTime();
                const targetDate = new Date(start.getTime() + offsetMs);
                targetDKey = getLocalDateKey(targetDate);
              }
              
              if (targetDKey && daysMap[targetDKey]) {
                daysMap[targetDKey].faturamentoAnterior += proportionalValGross;
                daysMap[targetDKey].faturamentoAnteriorNet += proportionalValNet;
                if (isMl) {
                  daysMap[targetDKey].mlFaturamentoAnterior += proportionalValGross;
                } else {
                  daysMap[targetDKey].siteFaturamentoAnterior += proportionalValGross;
                }
              }
            }
          }
        });
      });
      
      const sortedKeys = Object.keys(daysMap).sort();
      const list = sortedKeys.map(key => {
        const dayInfo = daysMap[key];
        return {
          name: dayInfo.label,
          "Faturamento": Math.round(dayInfo.faturamento),
          "Faturamento Sem Frete": Math.round(dayInfo.faturamentoNet),
          "Faturamento Anterior": Math.round(dayInfo.faturamentoAnterior),
          "Faturamento Anterior Sem Frete": Math.round(dayInfo.faturamentoAnteriorNet),
          "ML Faturamento": Math.round(dayInfo.mlFaturamento),
          "ML Faturamento Sem Frete": Math.round(dayInfo.mlFaturamentoNet),
          "ML Faturamento Anterior": Math.round(dayInfo.mlFaturamentoAnterior),
          "Site Faturamento": Math.round(dayInfo.siteFaturamento),
          "Site Faturamento Sem Frete": Math.round(dayInfo.siteFaturamentoNet),
          "Site Faturamento Anterior": Math.round(dayInfo.siteFaturamentoAnterior),
          "Meta": Math.round(dayInfo.goal),
          ordersCount: dayInfo.ordersSet.size,
          mlOrdersCount: dayInfo.mlOrdersSet.size,
          siteOrdersCount: dayInfo.siteOrdersSet.size
        };
      });
      
      return list;
    }
  }, [faturamentoOrders, goals, dateRange, startDate, endDate]);

  // --- FILTRAGEM E PAGINAÇÃO DE PEDIDOS DA AUDITORIA ---
  const financialFilteredOrders = useMemo(() => {
    let list = financialKpis.contributingOrders;
    if (financialSearchQuery.trim()) {
      const query = financialSearchQuery.toLowerCase();
      list = list.filter(o => 
        String(o.orderNumber).toLowerCase().includes(query) || 
        String(o.customerName).toLowerCase().includes(query)
      );
    }
    return list;
  }, [financialKpis.contributingOrders, financialSearchQuery]);

  const financialPaginatedOrders = useMemo(() => {
    const startIdx = (financialPage - 1) * 10;
    return financialFilteredOrders.slice(startIdx, startIdx + 10);
  }, [financialFilteredOrders, financialPage]);

  const getSelectedSkuDetails = () => {
    if (!selectedSkuDetail) return { active: [], sold: [], archived: [] };

    // 1. Active orders
    const active = data.filteredItems.filter((o) => {
      const parsed = parseSku(o.sku);
      if (groupBy === "course") return parsed.course === selectedSkuDetail;
      if (groupBy === "model") return parsed.model === selectedSkuDetail;
      return (o.sku || "").trim().toUpperCase() === selectedSkuDetail;
    });

    // 2. Sold items
    const soldFiltered = soldItems.filter((o) => {
      const parsed = parseSku(o.sku);
      if (groupBy === "course") return parsed.course === selectedSkuDetail;
      if (groupBy === "model") return parsed.model === selectedSkuDetail;
      return (o.sku || "").trim().toUpperCase() === selectedSkuDetail;
    });
    const sold = [...soldFiltered]
      .sort((a, b) => new Date(b.dateOut || 0) - new Date(a.dateOut || 0))
      .slice(0, 15);

    // 3. Archived orders
    const archivedFiltered = archivedItems.filter((o) => {
      const parsed = parseSku(o.sku);
      if (groupBy === "course") return parsed.course === selectedSkuDetail;
      if (groupBy === "model") return parsed.model === selectedSkuDetail;
      return (o.sku || "").trim().toUpperCase() === selectedSkuDetail;
    });
    const archived = [...archivedFiltered]
      .sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0))
      .slice(0, 15);

    return { active, sold, archived };
  };

  const performanceChartData = useMemo(() => {
    if (!selectedSkuDetail) return [];

    // Filter items matching the selection:
    // 1. Sold items in the chosen period
    const matchedSold = soldItems.filter((o) => {
      const parsed = parseSku(o.sku);
      if (groupBy === "course") return parsed.course === selectedSkuDetail;
      if (groupBy === "model") return parsed.model === selectedSkuDetail;
      return (o.sku || "").trim().toUpperCase() === selectedSkuDetail;
    });

    // 2. Production items in the chosen period (Active and Archived)
    const matchedActive = data.filteredItems.filter((o) => {
      const parsed = parseSku(o.sku);
      if (groupBy === "course") return parsed.course === selectedSkuDetail;
      if (groupBy === "model") return parsed.model === selectedSkuDetail;
      return (o.sku || "").trim().toUpperCase() === selectedSkuDetail;
    });

    const matchedArchived = archivedItems.filter((o) => {
      const parsed = parseSku(o.sku);
      if (groupBy === "course") return parsed.course === selectedSkuDetail;
      if (groupBy === "model") return parsed.model === selectedSkuDetail;
      return (o.sku || "").trim().toUpperCase() === selectedSkuDetail;
    });

    // Grouping by Date:
    // Determine the date range bounds
    let minDate = new Date();
    let maxDate = new Date();

    if (dateRange === "custom" && startDate && endDate) {
      minDate = new Date(startDate + "T00:00:00");
      maxDate = new Date(endDate + "T23:59:59.999");
    } else if (dateRange === "prev_month") {
      const now = new Date();
      minDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      maxDate = new Date(now.getFullYear(), now.getMonth() - 1, lastDay, 23, 59, 59, 999);
    } else if (dateRange !== "all") {
      if (dateRange === "current_month") {
        minDate.setDate(1);
      } else {
        const days = parseInt(dateRange, 10);
        minDate.setDate(minDate.getDate() - days);
      }
      minDate.setHours(0, 0, 0, 0);
    } else {
      // For "all", find the min date in the dataset
      const dates = [
        ...matchedSold.map(i => new Date(i.dateOut)),
        ...matchedActive.map(i => new Date(i.created || i.createdAt)),
        ...matchedArchived.map(i => new Date(i.created))
      ].filter(d => !isNaN(d.getTime()));

      if (dates.length > 0) {
        minDate = new Date(Math.min(...dates));
      } else {
        minDate.setDate(minDate.getDate() - 30); // fallback
      }
    }

    const diffDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
    let groupMode = "day"; // "day" or "month" or "week"
    if (diffDays > 45 && diffDays <= 180) {
      groupMode = "week";
    } else if (diffDays > 180) {
      groupMode = "month";
    }

    const dateMap = {};

    const getGroupKey = (d) => {
      if (groupMode === "month") {
        return d.toLocaleDateString("pt-BR", { month: "2-digit", year: "2-digit" }); // e.g. "05/26"
      }
      if (groupMode === "week") {
        // Simple week identifier: Year-Wxx
        const firstDayOfYear = new Date(d.getFullYear(), 0, 1);
        const pastDaysOfYear = (d - firstDayOfYear) / 86400000;
        const weekNum = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
        const yr = String(d.getFullYear()).substring(2);
        return `Sem ${weekNum}/${yr}`;
      }
      // Daily
      return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }); // e.g. "20/05"
    };

    // Populate dates in range to show zero values on empty periods
    if (groupMode === "day" && diffDays <= 45) {
      const temp = new Date(minDate);
      while (temp <= maxDate) {
        const key = getGroupKey(temp);
        dateMap[key] = { name: key, timestamp: temp.getTime(), sold: 0, production: 0 };
        temp.setDate(temp.getDate() + 1);
      }
    } else if (groupMode === "week" && diffDays <= 180) {
      const temp = new Date(minDate);
      while (temp <= maxDate) {
        const key = getGroupKey(temp);
        if (!dateMap[key]) {
          dateMap[key] = { name: key, timestamp: temp.getTime(), sold: 0, production: 0 };
        }
        temp.setDate(temp.getDate() + 7);
      }
    } else if (groupMode === "month") {
      const temp = new Date(minDate);
      while (temp <= maxDate) {
        const key = getGroupKey(temp);
        if (!dateMap[key]) {
          dateMap[key] = { name: key, timestamp: temp.getTime(), sold: 0, production: 0 };
        }
        temp.setMonth(temp.getMonth() + 1);
      }
    }

    // Now aggregate:
    // 1. Sold items
    matchedSold.forEach((item) => {
      const d = new Date(item.dateOut);
      if (!isNaN(d.getTime())) {
        const key = getGroupKey(d);
        if (!dateMap[key]) {
          dateMap[key] = { name: key, timestamp: d.getTime(), sold: 0, production: 0 };
        }
        dateMap[key].sold += 1;
      }
    });

    // 2. Production items (Active + Archived)
    matchedActive.forEach((item) => {
      const d = new Date(item.created || item.createdAt);
      if (!isNaN(d.getTime())) {
        const key = getGroupKey(d);
        if (!dateMap[key]) {
          dateMap[key] = { name: key, timestamp: d.getTime(), sold: 0, production: 0 };
        }
        dateMap[key].production += 1;
      }
    });

    matchedArchived.forEach((item) => {
      const d = new Date(item.created);
      if (!isNaN(d.getTime())) {
        const key = getGroupKey(d);
        if (!dateMap[key]) {
          dateMap[key] = { name: key, timestamp: d.getTime(), sold: 0, production: 0 };
        }
        dateMap[key].production += 1;
      }
    });

    // Sort chronologically by timestamp
    const sortedData = Object.values(dateMap).sort((a, b) => a.timestamp - b.timestamp);
    return sortedData;
  }, [selectedSkuDetail, soldItems, data, archivedItems, dateRange, startDate, endDate, groupBy]);

  const skuDetails = getSelectedSkuDetails();

  if (!data || loadingOrders || loadingEnriched)
    return (
      <div className="p-10 flex justify-center text-slate-400">
        <Activity className="animate-spin mr-2" /> Carregando Indicadores...
      </div>
    );

  return (
    <div className="p-4 md:p-6 bg-slate-50 h-full space-y-6 animate-fade-in pb-24 overflow-y-auto">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="text-purple-600" /> Dashboard Gerencial
            </h1>
            <p className="text-slate-500 text-xs mt-1">
              Visão completa da operação.
            </p>
          </div>
          
          <div className="flex bg-slate-100 p-1 rounded-lg gap-1 border border-slate-200 mt-2 md:mt-0 shrink-0">
            <button
              onClick={() => setCurrentView("general")}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${currentView === "general"
                  ? "bg-white text-purple-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                }`}
            >
              Indicadores Gerais
            </button>
            <button
              onClick={() => setCurrentView("financial")}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${currentView === "financial"
                  ? "bg-white text-purple-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                }`}
            >
              Faturamento & Metas
            </button>
            <button
              onClick={() => setCurrentView("items")}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${currentView === "items"
                  ? "bg-white text-purple-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                }`}
            >
              Itens & Vendas por SKU
            </button>
          </div>
        </div>
        
        <div className="flex flex-wrap justify-center bg-slate-100 p-1 rounded-lg gap-1">
          {[
            { l: "MÊS ATUAL", v: "current_month" },
            { l: "MÊS ANTERIOR", v: "prev_month" },
            { l: "HOJE", v: "0" },
            { l: "7D", v: "7" },
            { l: "30D", v: "30" },
            { l: "TODO FLUXO", v: "all" },
            { l: "PERSONALIZADO", v: "custom" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => {
                setSelectedRangeTab(o.v);
                if (o.v !== "custom") {
                  setDateRange(o.v);
                }
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${selectedRangeTab === o.v
                  ? "bg-white text-purple-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {selectedRangeTab === "custom" && (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-wrap items-center gap-3 animate-fade-in text-xs font-bold text-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">De:</span>
            <input
              type="date"
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyCustomDates();
              }}
              className="border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-purple-500 text-xs font-medium text-slate-800 bg-slate-50 focus:bg-white transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Até:</span>
            <input
              type="date"
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyCustomDates();
              }}
              className="border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-purple-500 text-xs font-medium text-slate-800 bg-slate-50 focus:bg-white transition-all"
            />
          </div>
          <button
            type="button"
            onClick={handleApplyCustomDates}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95 cursor-pointer ${
              hasPendingDateChanges
                ? "bg-purple-600 hover:bg-purple-700 text-white ring-2 ring-purple-300"
                : "bg-purple-600 hover:bg-purple-700 text-white"
            }`}
            title="Aplicar período selecionado"
          >
            <Check size={14} className="stroke-[2.5]" />
            OK
          </button>
          {hasPendingDateChanges && (
            <span className="text-[11px] font-semibold text-amber-600 flex items-center gap-1">
              ● Clique em OK para atualizar os dados
            </span>
          )}
        </div>
      )}

      {currentView === "general" ? (
        <>
          {/* KPI CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              title="Produção"
              value={data.totalItems}
              icon={Package}
              color="bg-blue-500"
            />
            <KpiCard
              title="Faturamento Est."
              value={formatMoney(data.totalRevenue)}
              icon={DollarSign}
              color="bg-emerald-500"
            />
            <KpiCard
              title="Exceções"
              value={data.totalExceptions}
              icon={AlertTriangle}
              color="bg-red-500"
              warning={data.totalExceptions > 0}
            />
            <KpiCard
              title="Produtividade"
              value={data.totalActions}
              icon={Users}
              color="bg-indigo-500"
              subtitle="Ações Registradas"
            />
          </div>

          {/* 2. LINHA DO TEMPO & SLA */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white p-5 rounded-xl shadow-sm border border-slate-200 h-[300px]">
              <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2 text-sm">
                <Calendar size={16} className="text-slate-400" /> Volume Diário
              </h3>
              <div className="h-[240px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trendData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Qtd"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 h-[300px] flex flex-col">
              <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2 text-sm">
                <Hourglass size={16} className="text-slate-400" /> SLA (Dias Úteis)
              </h3>
              <div className="flex-1 flex flex-col justify-center">
                {/* Pass only filtered items to AgeChart for correct calculations */}
                <AgeChart orders={data.filteredItems} />
              </div>
            </div>
          </div>

          {/* 3. UF, LOGÍSTICA & GARGALOS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 h-[320px]">
              <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2 text-xs uppercase">
                <MapPin size={14} /> Vendas por Estado
              </h3>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.ufData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {data.ufData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: "10px" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 h-[320px]">
              <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2 text-xs uppercase">
                <Truck size={14} /> Status Logístico
              </h3>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.logisticsData}
                    layout="vertical"
                    margin={{ left: 0, right: 20 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={110}
                      tick={{ fontSize: 9 }}
                      interval={0}
                    />
                    <Tooltip cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                      {data.logisticsData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[(index + 3) % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 h-[320px]">
              <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2 text-xs uppercase">
                <Clock size={14} /> Gargalos Produção
              </h3>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.productionData}
                    layout="vertical"
                    margin={{ left: 0, right: 10 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={90}
                      tick={{ fontSize: 9 }}
                      interval={0}
                    />
                    <Tooltip cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="value" fill="#82ca9d" radius={[0, 4, 4, 0]}>
                      {data.productionData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 4. TOP OPERADORES */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2 text-sm">
              <Users size={16} className="text-slate-400" /> Ranking de
              Produtividade (Logs)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {data.topUsers.map((u, idx) => (
                <div
                  key={idx}
                  className="flex flex-col items-center p-3 bg-slate-50 rounded-lg border border-slate-100 hover:bg-slate-100 transition-colors"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs mb-2 ${idx === 0
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-white border border-slate-200 text-slate-500"
                      }`}
                  >
                    {idx + 1}
                  </div>
                  <span className="font-bold text-slate-700 text-xs truncate w-full text-center mb-1">
                    {u.name}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium bg-white px-2 py-0.5 rounded border border-slate-200">
                    {u.count} ações
                  </span>
                </div>
              ))}
              {data.topUsers.length === 0 && (
                <div className="col-span-5 text-center py-4 text-slate-400 text-xs">
                  Sem dados de log recentes (Carregando:{" "}
                  {loadingLogs ? "Sim" : "Não"}).
                </div>
              )}
            </div>
          </div>
        </>
      ) : currentView === "financial" ? (
        <div className="space-y-6 animate-fade-in">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              title="Faturamento Real"
              value={formatMoney(financialKpis.totalRevenue)}
              icon={DollarSign}
              color="bg-emerald-500"
              subtitle={`Sem frete: ${formatMoney(financialKpis.totalRevenueNet)}`}
              action={(
                <div className="flex flex-col items-start gap-1">
                  <button
                    onClick={handleSyncMlOrders}
                    disabled={syncingMl}
                    className="px-2 py-0.5 mt-1 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 active:scale-95 transition-all text-emerald-700 border border-emerald-200 text-[9px] font-extrabold rounded shadow-sm uppercase tracking-wider flex items-center gap-1"
                  >
                    {syncingMl ? "Sincronizando..." : "Sincronizar ML"}
                  </button>
                  {syncMlProgress && (
                    <span className="text-[8px] font-semibold text-emerald-600 animate-pulse">{syncMlProgress}</span>
                  )}
                </div>
              )}
            />
            <KpiCard
              title="Meta do Período"
              value={formatMoney(financialKpis.totalGoal)}
              icon={TrendingUp}
              color="bg-purple-500"
              subtitle="Meta mensal estabelecida de fábrica"
              action={(
                <button
                  onClick={() => setIsEditGoalModalOpen(true)}
                  className="px-2 py-0.5 mt-1 bg-purple-50 hover:bg-purple-100 active:scale-95 transition-all text-purple-700 border border-purple-200 text-[9px] font-extrabold rounded shadow-sm uppercase tracking-wider flex items-center gap-1"
                >
                  Editar Meta
                </button>
              )}
            />
            <KpiCard
              title="Atingimento"
              value={`${financialKpis.progressPercent.toFixed(1)}%`}
              icon={Activity}
              color={financialKpis.progressPercent >= 100 ? "bg-green-600" : "bg-orange-500"}
              subtitle={financialKpis.progressPercent >= 100 ? "Meta batida! 🎉" : "Falta pouco"}
            />
            <KpiCard
              title="Ticket Médio"
              value={formatMoney(financialKpis.averageOrderValue)}
              icon={ShoppingCart}
              color="bg-blue-500"
              subtitle={`${financialKpis.totalOrdersCount} pedidos faturados`}
            />
          </div>

          {/* Progress Bar Card */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Progresso da Meta</span>
              <span className="text-sm font-black text-slate-800">
                {formatMoney(financialKpis.totalRevenue)} / {formatMoney(financialKpis.totalGoal)} ({financialKpis.progressPercent.toFixed(1)}%)
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden border border-slate-200">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  financialKpis.progressPercent >= 100 
                    ? "bg-gradient-to-r from-green-400 to-green-600" 
                    : financialKpis.progressPercent >= 50 
                    ? "bg-gradient-to-r from-amber-400 to-amber-600" 
                    : "bg-gradient-to-r from-purple-400 to-purple-600"
                }`}
                style={{ width: `${Math.min(100, financialKpis.progressPercent)}%` }}
              />
            </div>
            <p className="text-xs text-slate-400 mt-2 font-medium">
              {financialKpis.progressPercent >= 100 
                ? "🏆 Meta superada com sucesso! Excelente trabalho de vendas e produção!" 
                : financialKpis.progressPercent >= 50 
                ? "💪 Mais da metade do caminho percorrido. Foco total nas conversões!" 
                : "🚀 Início do progresso. Acelere as vendas de fábrica!"}
            </p>
          </div>

          {/* Chart Section */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
              <div>
                <h3 className="font-bold text-slate-700 flex items-center gap-2 text-sm">
                  <Calendar size={16} className="text-slate-400" /> Evolução de Vendas & Metas
                </h3>
                <p className="text-[10px] text-slate-400">
                  Comparativo diário de faturamento de fábrica em relação à meta diária e período anterior.
                </p>
              </div>
            </div>

            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={financialChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} dy={10} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip 
                    formatter={(value) => formatMoney(value)}
                    labelFormatter={(label, items) => {
                      const activePoint = items?.[0]?.payload;
                      const count = activePoint ? activePoint.ordersCount : 0;
                      const prefix = String(label).includes("/") ? "Mês" : "Dia";
                      return `${prefix} ${label} (${count} ${count === 1 ? 'pedido' : 'pedidos'})`;
                    }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: "10px", marginTop: "10px" }} />
                  <Bar
                    name="Faturamento"
                    dataKey="Faturamento"
                    fill="#10b981"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    name="Faturamento Sem Frete"
                    dataKey="Faturamento Sem Frete"
                    fill="#34d399"
                    hide
                  />
                  <Line
                    name="Faturamento Período Anterior"
                    type="monotone"
                    dataKey="Faturamento Anterior"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={false}
                  />
                  <Line
                    name="Meta"
                    type="monotone"
                    dataKey="Meta"
                    stroke="#a855f7"
                    strokeWidth={2.5}
                    dot={{ r: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Channel Analysis Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mercado Livre Card & Chart */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 flex flex-col space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <div>
                  <h4 className="font-extrabold text-slate-800 text-sm">Mercado Livre</h4>
                  <p className="text-[10px] text-slate-400">Pedidos que contêm #ML no número</p>
                </div>
                <span className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-100 rounded-full text-[10px] font-bold">
                  Canal Integrado
                </span>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center bg-slate-50/50 p-3 rounded-lg border border-slate-100">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Faturamento</p>
                  <p className="text-sm font-black text-slate-700 mt-0.5">{formatMoney(financialKpis.mlRevenue)}</p>
                  <p className="text-[8px] text-slate-400 font-medium">({formatMoney(financialKpis.mlRevenueNet)} sem frete)</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Ticket Médio</p>
                  <p className="text-sm font-black text-slate-700 mt-0.5">{formatMoney(financialKpis.mlAverageValue)}</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Pedidos</p>
                  <p className="text-sm font-black text-slate-700 mt-0.5">{financialKpis.mlOrdersCount}</p>
                </div>
              </div>
              
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={financialChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip 
                      formatter={(value) => formatMoney(value)}
                      labelFormatter={(label, items) => {
                        const activePoint = items?.[0]?.payload;
                        const count = activePoint ? activePoint.mlOrdersCount : 0;
                        const prefix = String(label).includes("/") ? "Mês" : "Dia";
                        return `${prefix} ${label} (${count} ${count === 1 ? 'pedido' : 'pedidos'})`;
                      }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: "9px", marginTop: "5px" }} />
                    <Bar name="Vendas ML" dataKey="ML Faturamento" fill="#d97706" radius={[3, 3, 0, 0]} />
                    <Bar name="ML Faturamento Sem Frete" dataKey="ML Faturamento Sem Frete" fill="#fbbf24" hide />
                    <Line name="ML Período Anterior" type="monotone" dataKey="ML Faturamento Anterior" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Site & Outros Card & Chart */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 flex flex-col space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <div>
                  <h4 className="font-extrabold text-slate-800 text-sm">Site & Outros</h4>
                  <p className="text-[10px] text-slate-400">Pedidos próprios, showroom e outros canais</p>
                </div>
                <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full text-[10px] font-bold">
                  Canais Próprios
                </span>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center bg-slate-50/50 p-3 rounded-lg border border-slate-100">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Faturamento</p>
                  <p className="text-sm font-black text-slate-700 mt-0.5">{formatMoney(financialKpis.siteRevenue)}</p>
                  <p className="text-[8px] text-slate-400 font-medium">({formatMoney(financialKpis.siteRevenueNet)} sem frete)</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Ticket Médio</p>
                  <p className="text-sm font-black text-slate-700 mt-0.5">{formatMoney(financialKpis.siteAverageValue)}</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Pedidos</p>
                  <p className="text-sm font-black text-slate-700 mt-0.5">{financialKpis.siteOrdersCount}</p>
                </div>
              </div>
              
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={financialChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip 
                      formatter={(value) => formatMoney(value)}
                      labelFormatter={(label, items) => {
                        const activePoint = items?.[0]?.payload;
                        const count = activePoint ? activePoint.siteOrdersCount : 0;
                        const prefix = String(label).includes("/") ? "Mês" : "Dia";
                        return `${prefix} ${label} (${count} ${count === 1 ? 'pedido' : 'pedidos'})`;
                      }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: "9px", marginTop: "5px" }} />
                    <Bar name="Vendas Site" dataKey="Site Faturamento" fill="#2563eb" radius={[3, 3, 0, 0]} />
                    <Bar name="Site Faturamento Sem Frete" dataKey="Site Faturamento Sem Frete" fill="#818cf8" hide />
                    <Line name="Site Período Anterior" type="monotone" dataKey="Site Faturamento Anterior" stroke="#60a5fa" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Audit Table Section */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50/50">
              <div>
                <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                  <Layers size={18} className="text-purple-600" /> Auditoria de Pedidos
                </h3>
                <p className="text-xs text-slate-500 mt-1">Lista completa de pedidos de fábrica faturados no período.</p>
              </div>

              {/* Search input */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Buscar pedido ou cliente..."
                  value={financialSearchQuery}
                  onChange={(e) => {
                    setFinancialSearchQuery(e.target.value);
                    setFinancialPage(1);
                  }}
                  className="pl-9 pr-4 py-2 w-full text-xs border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto bg-white">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 font-bold uppercase text-[10px] tracking-wider border-b border-slate-100">
                    <th className="py-3 px-4">Pedido</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Data Registro</th>
                    <th className="py-3 px-4 text-center">Itens Fábrica</th>
                    <th className="py-3 px-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {financialPaginatedOrders.map((order, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-4 font-bold text-slate-700">#{order.orderNumber}</td>
                      <td className="py-3 px-4 font-medium text-slate-600">{order.customerName}</td>
                      <td className="py-3 px-4 text-slate-400">{order.date}</td>
                      <td className="py-3 px-4 text-center font-medium text-slate-600">
                        {order.factoryItems} de {order.totalItems}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${
                          order.status === "CANCELADO" || order.status === "GOLPE"
                            ? "bg-red-50 text-red-600 border border-red-100"
                            : order.archived
                            ? "bg-slate-100 text-slate-600 border border-slate-200"
                            : "bg-green-50 text-green-700 border border-green-100"
                        }`}>
                          {order.status === "CANCELADO" || order.status === "GOLPE" 
                            ? order.status 
                            : order.archived 
                            ? "ARQUIVADO" 
                            : "ATIVO"}
                        </span>
                      </td>
                    </tr>
                  ))}

                  {financialPaginatedOrders.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-slate-400">
                        Nenhum pedido faturado encontrado para os filtros selecionados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Pagination */}
            {financialFilteredOrders.length > 10 && (
              <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/30 text-xs">
                <span className="text-slate-400">
                  Mostrando <span className="font-bold text-slate-700">{Math.min(financialFilteredOrders.length, (financialPage - 1) * 10 + 1)}-{Math.min(financialFilteredOrders.length, financialPage * 10)}</span> de <span className="font-bold text-slate-700">{financialFilteredOrders.length}</span> pedidos
                </span>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFinancialPage(prev => Math.max(1, prev - 1))}
                    disabled={financialPage === 1}
                    className="p-1 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="font-bold text-slate-700">
                    Página {financialPage} de {Math.ceil(financialFilteredOrders.length / 10)}
                  </span>
                  <button
                    onClick={() => setFinancialPage(prev => Math.min(Math.ceil(financialFilteredOrders.length / 10), prev + 1))}
                    disabled={financialPage === Math.ceil(financialFilteredOrders.length / 10)}
                    className="p-1 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* VISTA DE PRODUTOS E VENDAS POR SKU/CURSO/MODELO */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-col lg:flex-row justify-between items-center gap-4 bg-slate-50/50">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full lg:w-auto">
              <div>
                <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                  <Layers size={18} className="text-purple-600" /> Análise de Itens & Vendas
                </h3>
                <p className="text-slate-400 text-xs mt-0.5 font-medium">
                  Resumo de quantidades por {groupBy === "sku" ? "SKU" : groupBy === "course" ? "Curso" : "Modelo"} no período selecionado.
                </p>
              </div>
              
              {/* Seleção de Agrupamento */}
              <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-extrabold shrink-0 self-start sm:self-center">
                <button
                  onClick={() => { setGroupBy("sku"); setSortBy("sold"); }}
                  className={`px-3 py-1 rounded-md transition-all ${groupBy === "sku" ? "bg-white text-purple-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                >
                  Por SKU
                </button>
                <button
                  onClick={() => { setGroupBy("course"); setSortBy("sold"); }}
                  className={`px-3 py-1 rounded-md transition-all ${groupBy === "course" ? "bg-white text-purple-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                >
                  Por Curso
                </button>
                <button
                  onClick={() => { setGroupBy("model"); setSortBy("sold"); }}
                  className={`px-3 py-1 rounded-md transition-all ${groupBy === "model" ? "bg-white text-purple-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                >
                  Por Modelo
                </button>
              </div>
            </div>

            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder={groupBy === "sku" ? "Filtrar por SKU..." : groupBy === "course" ? "Filtrar por Curso..." : "Filtrar por Modelo..."}
                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                value={searchSku}
                onChange={(e) => setSearchSku(e.target.value)}
              />
            </div>
          </div>

          {loadingEnriched ? (
            <div className="p-12 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
              <Activity className="animate-spin text-purple-600" size={24} />
              <span>Carregando dados de vendas e histórico...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-50 text-slate-500 font-bold uppercase border-b border-slate-100 select-none">
                  <tr>
                    <th
                      onClick={() => setSortBy("key")}
                      className="p-4 cursor-pointer hover:bg-slate-100 transition-colors"
                    >
                      <span className="flex items-center gap-1">
                        {groupBy === "sku" ? "SKU" : groupBy === "course" ? "Curso" : "Modelo"} <ArrowUpDown size={12} />
                      </span>
                    </th>
                    <th
                      onClick={() => setSortBy("sold")}
                      className="p-4 cursor-pointer hover:bg-slate-100 transition-colors text-center"
                    >
                      <span className="flex items-center justify-center gap-1 text-emerald-700">
                        <ShoppingCart size={12} /> Vendidos (Entregas) <ArrowUpDown size={12} />
                      </span>
                    </th>
                    <th
                      onClick={() => setSortBy("production")}
                      className="p-4 cursor-pointer hover:bg-slate-100 transition-colors text-center"
                    >
                      <span className="flex items-center justify-center gap-1 text-blue-700">
                        <Package size={12} /> Em Produção <ArrowUpDown size={12} />
                      </span>
                    </th>
                    <th
                      onClick={() => setSortBy("archived")}
                      className="p-4 cursor-pointer hover:bg-slate-100 transition-colors text-center"
                    >
                      <span className="flex items-center justify-center gap-1 text-purple-700">
                        <History size={12} /> Finalizados/Arquivados <ArrowUpDown size={12} />
                      </span>
                    </th>
                    <th
                      onClick={() => setSortBy("total")}
                      className="p-4 cursor-pointer hover:bg-slate-100 transition-colors text-center"
                    >
                      <span className="flex items-center justify-center gap-1 text-slate-700 font-extrabold">
                        Total Acumulado <ArrowUpDown size={12} />
                      </span>
                    </th>
                    <th className="p-4 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {skuAnalysis.map((item) => (
                    <tr key={item.key} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors font-medium">
                      <td className="p-4 font-mono font-bold text-slate-700">{item.key}</td>
                      <td className="p-4 text-center">
                        <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 rounded-full font-bold">
                          {item.sold}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 rounded-full font-bold">
                          {item.production}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="bg-purple-50 text-purple-700 border border-purple-100 px-2.5 py-1 rounded-full font-bold">
                          {item.archived}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-1 rounded-full font-bold">
                          {item.total}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => setSelectedSkuDetail(item.key)}
                          className="px-3 py-1.5 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all inline-flex items-center gap-1 font-bold"
                          title="Ver Detalhes"
                        >
                          <Eye size={12} /> Detalhar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {skuAnalysis.length === 0 && (
                    <tr>
                      <td colSpan="6" className="p-12 text-center text-slate-400">
                        Nenhum resultado encontrado com os filtros aplicados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL DETALHAMENTO SKU/CURSO/MODELO */}
      {selectedSkuDetail && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <span className="text-[10px] bg-purple-100 text-purple-700 font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  Detalhamento de Fluxo ({groupBy === "sku" ? "SKU" : groupBy === "course" ? "Curso" : "Modelo"})
                </span>
                <h3 className="text-base font-black text-slate-800 mt-1 font-mono">
                  {selectedSkuDetail}
                </h3>
              </div>
              <button
                onClick={() => setSelectedSkuDetail(null)}
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-lg transition-all"
              >
                <XCircle size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar flex-1">
              {/* Gráfico de Desempenho */}
              <div className="bg-slate-50 p-4 border border-slate-200 rounded-xl">
                <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-3 bg-purple-600 rounded-full"></span>
                  Desempenho no Período Selecionado
                </h4>
                <div className="h-[200px] w-full">
                  {performanceChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={performanceChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 9 }}
                          axisLine={false}
                          tickLine={false}
                          dy={8}
                        />
                        <YAxis
                          tick={{ fontSize: 9 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          formatter={(value) => formatMoney(value)}
                          contentStyle={{
                            borderRadius: "8px",
                            border: "none",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: "10px", paddingTop: "5px" }} />
                        <Line
                          type="monotone"
                          name="Vendas (Entregas)"
                          dataKey="sold"
                          stroke="#10b981"
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          type="monotone"
                          name="Ordens de Produção"
                          dataKey="production"
                          stroke="#3b82f6"
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400 text-xs italic">
                      Sem dados de vendas ou produção no período selecionado.
                    </div>
                  )}
                </div>
              </div>

              {/* Seção 1: Produção Ativa */}
              <div>
                <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-3 bg-blue-500 rounded-full"></span>
                  Ordens de Produção Ativas ({skuDetails.active.length})
                </h4>
                <div className="bg-slate-50 border border-slate-100 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-500 font-bold uppercase border-b border-slate-200">
                        <tr>
                          <th className="p-3">Pedido</th>
                          <th className="p-3">Cliente</th>
                          <th className="p-3">SKU</th>
                          <th className="p-3">Etapa Kanban</th>
                          <th className="p-3">Status Logística</th>
                          <th className="p-3">Criado em</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium">
                        {skuDetails.active.map(o => (
                          <tr key={o.id} className="hover:bg-white transition-colors">
                            <td className="p-3 font-mono font-bold text-purple-600">
                              {o.order_number || o.orderNumber || "S/N"}
                            </td>
                            <td className="p-3 text-slate-700 font-bold">{o.customer_name || "ND"}</td>
                            <td className="p-3 font-mono text-slate-500">{o.sku}</td>
                            <td className="p-3">
                              <span className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                {o.status ? o.status.replace("ESTOQUE_", "").replace("_", " ") : "SOLICITACAO"}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-[10px] font-bold">
                                {o.logisticsStatus || "SEM ETIQUETA"}
                              </span>
                            </td>
                            <td className="p-3 text-slate-500">
                              {new Date(o.created || o.createdAt).toLocaleDateString("pt-BR")}
                            </td>
                          </tr>
                        ))}
                        {skuDetails.active.length === 0 && (
                          <tr>
                            <td colSpan="6" className="p-6 text-center text-slate-400 italic">
                              Nenhum item em produção ativa no momento.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Seção 2: Vendas Recentes */}
              <div>
                <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-3 bg-emerald-500 rounded-full"></span>
                  Últimas Vendas Registradas ({skuDetails.sold.length}) {skuDetails.sold.length >= 15 && <span className="text-[10px] text-slate-400 normal-case font-normal">(Mostrando as 15 mais recentes)</span>}
                </h4>
                <div className="bg-slate-50 border border-slate-100 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-500 font-bold uppercase border-b border-slate-200">
                        <tr>
                          <th className="p-3">Data da Venda</th>
                          <th className="p-3">SKU</th>
                          <th className="p-3">Operador / Vendedor</th>
                          <th className="p-3">ID Registro</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium">
                        {skuDetails.sold.map(item => (
                          <tr key={item.id} className="hover:bg-white transition-colors">
                            <td className="p-3 text-slate-700">
                              {item.dateOut ? new Date(item.dateOut).toLocaleString("pt-BR") : "-"}
                            </td>
                            <td className="p-3 font-mono text-slate-600 font-bold">{item.sku}</td>
                            <td className="p-3 text-slate-700 font-bold flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                              {item.soldBy || "Sistema"}
                            </td>
                            <td className="p-3 text-slate-400 font-mono text-[10px]">{item.id}</td>
                          </tr>
                        ))}
                        {skuDetails.sold.length === 0 && (
                          <tr>
                            <td colSpan="4" className="p-6 text-center text-slate-400 italic">
                              Nenhuma venda registrada no período selecionado.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Seção 3: Histórico de Produção Arquivado */}
              <div>
                <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-3 bg-purple-500 rounded-full"></span>
                  Histórico Finalizado/Arquivado ({skuDetails.archived.length}) {skuDetails.archived.length >= 15 && <span className="text-[10px] text-slate-400 normal-case font-normal">(Mostrando as 15 mais recentes)</span>}
                </h4>
                <div className="bg-slate-50 border border-slate-100 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-500 font-bold uppercase border-b border-slate-200">
                        <tr>
                          <th className="p-3">Pedido</th>
                          <th className="p-3">Cliente</th>
                          <th className="p-3">SKU</th>
                          <th className="p-3">Último Status</th>
                          <th className="p-3">Criado em</th>
                          <th className="p-3">Arquivado em</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium font-mono">
                        {skuDetails.archived.map(o => (
                          <tr key={o.id} className="hover:bg-white transition-colors">
                            <td className="p-3 font-bold text-purple-600">
                              {o.order_number || o.orderNumber || "S/N"}
                            </td>
                            <td className="p-3 text-slate-700 font-bold font-sans">{o.customerName || o.customer_name || "ND"}</td>
                            <td className="p-3 text-slate-500">{o.sku}</td>
                            <td className="p-3 text-slate-600 font-bold font-sans">{o.status || "ARQUIVADO"}</td>
                            <td className="p-3 text-slate-500 font-sans">
                              {new Date(o.created || o.createdAt).toLocaleDateString("pt-BR")}
                            </td>
                            <td className="p-3 text-slate-500 font-sans">
                              {new Date(o.updated).toLocaleDateString("pt-BR")}
                            </td>
                          </tr>
                        ))}
                        {skuDetails.archived.length === 0 && (
                          <tr>
                            <td colSpan="6" className="p-6 text-center text-slate-400 font-sans italic">
                              Nenhum histórico arquivado no período selecionado.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
              <button
                onClick={() => setSelectedSkuDetail(null)}
                className="px-5 py-2 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-700 transition-colors uppercase tracking-wider"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR META */}
      {isEditGoalModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-md flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h3 className="text-base font-black text-slate-800 flex items-center gap-2">
                  <TrendingUp size={18} className="text-purple-600" /> Editar Meta Mensal
                </h3>
                <p className="text-[10px] text-slate-400 mt-0.5">Defina a meta de faturamento de fábrica para o mês</p>
              </div>
              <button
                onClick={() => setIsEditGoalModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-lg transition-all"
              >
                <XCircle size={20} />
              </button>
            </div>

            {/* Content */}
            <form onSubmit={handleSaveGoal} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Mês</label>
                  <select
                    value={editGoalMonth}
                    onChange={(e) => setEditGoalMonth(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    {[
                      { v: 1, l: "Janeiro" },
                      { v: 2, l: "Fevereiro" },
                      { v: 3, l: "Março" },
                      { v: 4, l: "Abril" },
                      { v: 5, l: "Maio" },
                      { v: 6, l: "Junho" },
                      { v: 7, l: "Julho" },
                      { v: 8, l: "Agosto" },
                      { v: 9, l: "Setembro" },
                      { v: 10, l: "Outubro" },
                      { v: 11, l: "Novembro" },
                      { v: 12, l: "Dezembro" },
                    ].map(m => (
                      <option key={m.v} value={m.v}>{m.l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Ano</label>
                  <input
                    type="number"
                    value={editGoalYear}
                    onChange={(e) => setEditGoalYear(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                    min="2020"
                    max="2050"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Novo Valor da Meta (R$)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400 text-xs font-bold">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editGoalValue}
                    onChange={(e) => setEditGoalValue(e.target.value)}
                    className="pl-9 pr-4 py-2 w-full text-xs font-bold border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                    required
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditGoalModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold transition-all uppercase"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSavingGoal}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg text-xs font-bold transition-all uppercase flex items-center gap-1 shadow-md shadow-purple-200"
                >
                  {isSavingGoal ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, color, warning, subtitle, action }) {
  return (
    <div
      className={`bg-white p-4 rounded-xl shadow-sm border ${warning ? "border-red-400 ring-1 ring-red-50" : "border-slate-200"
        } flex items-center justify-between`}
    >
      <div>
        <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">
          {title}
        </p>
        <h3
          className={`text-xl font-black ${warning ? "text-red-600" : "text-slate-800"
            }`}
        >
          {value}
        </h3>
        {subtitle && (
          <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>
        )}
        {action && (
          <div className="mt-2">{action}</div>
        )}
      </div>
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-md ${color}`}
      >
        <Icon size={20} />
      </div>
    </div>
  );
}
